import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { resetStep, setIsComingFromNonUserRoute } from "../redux/slice/stepSlice";
import { setCoverImages, setImages } from "../redux/slice/sketchSlice";
import { pruneSelectedPlanLocalStorage } from "../services/shopifyCartService";
import { selectBooksLoadedAt } from "../redux/slice/profileSlice";
import { isEtsyUser, isEtsySession } from "../services/etsyService";

/**
 * What the customer menu needs to know and do. Pulled out of
 * `shared/DefaultHeader.js` so the redesigned header can reuse it unchanged:
 * the cart count (from the local cart), when My Books is worth showing, and
 * the two navigations that reset wizard state on the way.
 */

const itemQuantity = (item) => {
  if (!item) return 0;
  const pdQty = Number(item?.planPricingData?.quantity);
  if (!Number.isNaN(pdQty) && pdQty > 0) return pdQty;
  const rootQty = Number(item?.quantity);
  if (!Number.isNaN(rootQty) && rootQty > 0) return rootQty;
  return 0;
};

const countCart = (items) =>
  Array.isArray(items) ? items.reduce((sum, item) => sum + (itemQuantity(item) || 1), 0) : 0;

/** The local cart lines, or [] when there are none or they can't be read. */
export const readLocalCartLines = () => {
  try {
    const raw = localStorage.getItem("selectedPlan");
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) return [parsed];
      return Array.isArray(parsed) ? parsed : [];
    }
    const unassigned = localStorage.getItem("unassignedPlans");
    if (unassigned) {
      const parsed = JSON.parse(unassigned);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) return [parsed];
      return Array.isArray(parsed) ? parsed : [];
    }
  } catch (error) {
    console.error("Error reading the local cart:", error);
  }
  return [];
};

export default function useCustomerMenu() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const Profile = useSelector((state) => state?.profile?.data);
  const { myBooks } = useSelector((state) => state?.profile);
  const booksLoadedAt = useSelector(selectBooksLoadedAt);
  const { activeBookId } = useSelector((state) => state?.stepFlow);
  const [cartCount, setCartCount] = useState(0);

  useEffect(() => {
    const read = () => {
      // No-op unless this document's book list is authoritative for the signed-in customer.
      pruneSelectedPlanLocalStorage(myBooks || [], booksLoadedAt);
      setCartCount(countCart(readLocalCartLines()));
    };
    read();
    window.addEventListener("storage", read);
    window.addEventListener("cartUpdated", read);
    return () => {
      window.removeEventListener("storage", read);
      window.removeEventListener("cartUpdated", read);
    };
  }, [booksLoadedAt, myBooks]);

  const orderedBooks = (myBooks || []).filter((book) => !!book.isOrderPlaces);
  // New customers with one book are kept in that book's flow; once there are
  // several (or an order), My Books is the only way back to the others.
  const showMyBooks = orderedBooks.length > 0 || (myBooks || []).length > 1;

  const goToMyBooks = useCallback(() => {
    if (!!activeBookId && showMyBooks) {
      dispatch(resetStep());
      dispatch(setCoverImages([]));
      dispatch(setImages([]));
      if (location.pathname === "/") dispatch(setIsComingFromNonUserRoute(true));
    } else if (!activeBookId && location.pathname.includes("create-book") && (myBooks || []).length <= 1) {
      return;
    }
    navigate("/dashboard");
  }, [activeBookId, showMyBooks, dispatch, location.pathname, myBooks, navigate]);

  /** Go to Finalize Order; returns false (and goes nowhere) when the cart is empty. */
  const goToFinalizeOrder = useCallback(() => {
    if (!readLocalCartLines().length) return false;
    navigate("/finalize-order");
    return true;
  }, [navigate]);

  return {
    cartCount,
    showMyBooks,
    goToMyBooks,
    goToFinalizeOrder,
    email: Profile?.email || null,
    isAuthenticated: !!Profile?._id,
    isEtsy: isEtsyUser(Profile) || isEtsySession(),
  };
}
